'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import type { BlockProps, ComponentBaseProps } from '@sola/schema/lib/schema.ts';
import { AdditionalSettingsArticleList } from '@sola/schema/lib/schema.ts';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import { compactObject } from '@sola/utils/object.ts';
import { FeaturedArticleCardSkeleton } from '../../../molecules/feature-article-card/feature-article-card-skeleton.tsx';
import { FeaturedArticleCard } from '../../../molecules/feature-article-card/feature-article-card.tsx';
const queryClient = new QueryClient();
export type ArticleListProfoundProps = BlockProps & ComponentBaseProps;
const ArticleListProfoundSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <section className="component component--no-header"><div className="container"><FeaturedArticleCardSkeleton /></div></section>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const ArticleListProfound: React.FC<ArticleListProfoundProps> = t0 => {
  const $ = _c(15);
  const {
    seoLevel: t1,
    settings
  } = t0;
  const seoLevel = t1 === undefined ? 3 : t1;
  let articleListSettings;
  let t2;
  let t3;
  let t4;
  if ($[0] !== settings) {
    articleListSettings = settings ? AdditionalSettingsArticleList.parse(settings) : undefined;
    const allFilters = {
      "taxonomy.articleTopics.keyword": articleListSettings?.topic,
      "taxonomy.articleThemes.keyword": articleListSettings?.theme,
      "taxonomy.programCountry.keyword": articleListSettings?.country
    };
    const compactFilters = compactObject(allFilters);
    t4 = useListArticlesQuery;
    t2 = JSON.stringify({
      "taxonomy.other.keyword": "promotedContent",
      "taxonomy.articleType.keyword": articleListSettings?.articleType || undefined
    });
    t3 = JSON.stringify(compactFilters);
    $[0] = settings;
    $[1] = articleListSettings;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    articleListSettings = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  const t5 = articleListSettings?.limit || 1;
  let t6;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t5) {
    t6 = {
      must: t2,
      filters: t3,
      perPage: t5,
      sort: "datePublished",
      sortDir: "desc"
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data,
    isLoading
  } = t4(t6);
  if (isLoading && !data) {
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ArticleListProfoundSkeleton />;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  const article = data?.items?.[0];
  if (!article) {
    return null;
  }
  let t7;
  if ($[10] !== article) {
    t7 = extractArgsSearchResult(article);
    $[10] = article;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const cardProps = t7;
  let t8;
  if ($[12] !== cardProps || $[13] !== seoLevel) {
    t8 = <section className="component component--no-header"><div className="container"><FeaturedArticleCard {...cardProps} seoLevel={seoLevel} /></div></section>;
    $[12] = cardProps;
    $[13] = seoLevel;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export const ArticleListProfoundProvider: React.FC<BlockProps> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ArticleListProfoundSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}><ArticleListProfound {...props} /></QueryClientProvider></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};